import { useContext, type MouseEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AnimatePhaseContext } from "./AnimatePhaseContext";

interface FullWindowLayerProps {
  /** The dim layer's classes. Callers keep their own color, padding, and z-index. */
  className: string;
  /** Close when the dim layer itself is clicked. Omit to ignore that click. */
  onBackdropClick?: () => void;
  children: ReactNode;
}

/**
 * A dim layer that covers the browser window, drawn on `document.body`.
 *
 * The task dialog's own dim layer is a flex box with `backdrop-filter`.
 * That box becomes the containing block for a `position: fixed` child, and
 * flex centering then shrinks the child to its panel. A second dialog left
 * inside the task dialog therefore dims only its own panel, and the task
 * dialog stays sharp around it. A portal keeps this layer on the window.
 *
 * The phase wrapper travels with the layer. Exit animation looks for
 * `data-animate-phase` on an ancestor, and a portal is not a DOM child of
 * `AnimatePresence`.
 */
export function FullWindowLayer({
  className,
  onBackdropClick,
  children,
}: FullWindowLayerProps) {
  const phase = useContext(AnimatePhaseContext);

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    if (onBackdropClick && e.target === e.currentTarget) onBackdropClick();
  };

  return createPortal(
    <div data-animate-phase={phase} style={{ display: "contents" }}>
      <div
        className={className}
        onClick={onBackdropClick ? handleClick : undefined}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
